// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonSizeVariants } from "@insite/mobius/Button";
import { FormFieldIcon, FormFieldPresentationPropsCommon } from "@insite/mobius/FormField/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import get from "@insite/mobius/utilities/get";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { css } from "styled-components";

export interface VariantValues {
    fontSize: number;
    height: number;
    icon: number;
    iconPadding: number;
    descriptionLineSpacing: number;
    borderRadius: number;
    datePickerWidth: number;
    leftLabelPadding: number;
    button: ButtonSizeVariants;
    labelHeight: number;
}

interface SizeVariantValues {
    default: VariantValues;
    small: VariantValues;
}

export const sizeVariantValues: SizeVariantValues = {
    default: {
        fontSize: fontConfig.small.size,
        height: 34,
        icon: 14,
        iconPadding: 8,
        descriptionLineSpacing: 4,
        borderRadius: 17,
        datePickerWidth: 145,
        leftLabelPadding: 8,
        button: "medium",
        labelHeight: 17,
    },
    small: {
        fontSize: fontConfig.small.size,
        height: 26,
        icon: 10,
        iconPadding: 6,
        descriptionLineSpacing: 4,
        borderRadius: 13,
        datePickerWidth: 118,
        leftLabelPadding: 8,
        button: "small",
        labelHeight: 17,
    },
};

export const borderTypes = {
    error: {
        color: "colors.danger.main",
        width: 1,
        borderPaddingRule: "padding: 0 12px 0 12px;",
        textAreaBorderPaddingRule: "padding: 12px;",
        underlinePaddingRule: "padding: 0 12px;",
        textAreaUnderlinePaddingRule: "padding: 12px;",
    },
    focus: {
        color: "colors.common.ui11",
        width: 1,
        borderPaddingRule: "padding: 0 12px 0 12px;",
        textAreaBorderPaddingRule: "padding: 12px;",
        underlinePaddingRule: "padding: 0 12px;",
        textAreaUnderlinePaddingRule: "padding: 12px;",
    },
    inactive: {
        color: "colors.common.border",
        width: 1,
        borderPaddingRule: "padding: 0 12px 0 12px;",
        textAreaBorderPaddingRule: "padding: 12px;",
        underlinePaddingRule: "padding: 0 12px 1px 12px;",
        textAreaUnderlinePaddingRule: "padding: 12px;",
    },
} as const;

type BorderKind = FormFieldPresentationPropsCommon["border"];

const borderByState = (
    {
        border,
        _sizeVariant,
        theme,
    }: {
        border: BorderKind;
        _sizeVariant: keyof typeof sizeVariantValues;
        theme: BaseTheme;
    },
    state: keyof typeof borderTypes,
    textarea = false,
) => {
    const {
        width,
        color,
        borderPaddingRule,
        underlinePaddingRule,
        textAreaBorderPaddingRule,
        textAreaUnderlinePaddingRule,
    } = borderTypes[state];
    let borderStyle = "solid";
    if (state === "focus") {
        borderStyle = get(theme, "focus.style");
    }
    const colorProp = get(theme, color);
    /* Border radius of 0 is included to override macOS select default 5px radius */
    if (border === "rectangle") {
        return `border: ${width}px ${borderStyle} ${colorProp};
        border-radius: ${sizeVariantValues[_sizeVariant].borderRadius}px;
        ${textarea ? textAreaBorderPaddingRule : borderPaddingRule}`;
    }
    if (border === "rounded") {
        return `border: ${width}px ${borderStyle} ${colorProp};
        border-radius: ${sizeVariantValues[_sizeVariant].borderRadius}px;
        ${textarea ? textAreaBorderPaddingRule : borderPaddingRule}`;
    }
    return `border-style: ${borderStyle};
    border-width: 0 0 ${width}px 0;
    border-color: ${colorProp};
    ${textarea ? textAreaUnderlinePaddingRule : underlinePaddingRule}
    border-radius: 0;`;
};

export const disabledByType = ({ border, theme }: { border: BorderKind; theme: BaseTheme }) => {
    if (border === "rectangle" || border === "rounded") {
        return css`
            opacity: 0.33;
            background: ${get(theme, "colors.common.ui2")};
            color: ${get(theme, "colors.text.main")};
            border: 1px solid ${get(theme, "colors.common.ui5")};
            &
                + ${/* sc-selector */ FormFieldIcon},
                &
                + button
                ${/* sc-selector */ FormFieldIcon},
                &
                + .clickable-wrapper
                button
                ${/* sc-selector */ FormFieldIcon} {
                background: transparent;
                opacity: 0.33;
            }
        `;
    }

    return "border-bottom-style: dotted;";
};

export default borderByState;
